import pluralize from "pluralize";
import { path, repeat } from "ramda";
import { escapeString, formatNumberByThousands, unescapeString } from "widgets/utils";

import type { AdminBrowser, ContentBrowser, Database, Organism, SavedItemsListing } from "../graphql.js";
import {
  defaultMapOfAcronyms,
  ENTITY_TYPE,
  ITEMS_TYPE,
  TRACK_TYPE,
  UNKNOWN_GENOME_NAME,
} from "../settings/settings.js";
import type { ViewerTrack } from "../ui-modules/genome-viewer/new/_mod.js";
import { compareBasicTypes } from "./sort.js";

export function lowerCaseFirstLetter(label: string): string {
  return label && label.charAt(0).toLowerCase() + label.slice(1);
}

export interface IStringComparisonConfiguration {
  ignoreCase: boolean;
  ignoreAccents: boolean;
  trim: boolean;
}

// Add some heuristics. Please feel free to challenge this!
pluralize.addUncountableRule(/\s+$/); // Ignore if string is empty
pluralize.addUncountableRule(/\d+$/); // Ignore if string end with number(s)
pluralize.addUncountableRule(/.+ \w{1,2}$/); // Ignore if name ends with a one or two character-word
pluralize.addUncountableRule(/\b(?:[A-Z]+){2,}$/); // Ignore if last word of name is an acronym with at least 2 letters

export function compareStrings(option1: string, option2: string, config?: IStringComparisonConfiguration) {
  if (!option1 && !option2) {
    return true;
  }
  return normalizeString(option1, config) === normalizeString(option2, config);
}

export function normalizeString(string: string, config?: IStringComparisonConfiguration): string {
  const ignoreCase: boolean = config?.ignoreCase ?? true;
  const ignoreAccents: boolean = config?.ignoreAccents ?? false;
  const trim: boolean = config?.trim ?? true;

  string = ignoreCase ? string.toLowerCase() : string;
  string = ignoreAccents ? string.normalize("NFD").replace(/[\u0300-\u036f]/g, "") : string;
  string = trim ? string.trim() : string;

  return string;
}

// simple comparison function for string values to be used e.g. in .sort, based on two strings and an option to sort descending
// For other scalar types there is compareBasicValues
export function compare(
  a: string | null | undefined,
  b: string | null | undefined,
  ascending = true,
  ignoreCase = false,
  nullEqualsUndefined = true,
): number {
  const aHasValue = !!a; // test for existence works since type is string and not any
  const bHasValue = !!b;
  // Handle most usual case with values first
  if (aHasValue && bHasValue) {
    if (ignoreCase) {
      a = a.toLowerCase();
      b = b.toLowerCase();
    }

    if (a < b) {
      return ascending ? -1 : 1;
    }

    if (a > b) {
      return ascending ? 1 : -1;
    }

    return 0;
  }
  // At least one side is null or undefined.
  if (aHasValue) {
    // a > b, b is null or undefined
    return ascending ? 1 : -1;
  }

  if (bHasValue) {
    // a < b, a is null or undefined
    return ascending ? -1 : 1;
  }

  // A and B are either null or undefined
  if (nullEqualsUndefined) {
    return 0; // null is equal undefined
  }

  // Order of values is:  null < undefined < any-value
  //  reasoning: we want to tell null and undefined apart.
  const aIsNull: boolean = a === null;
  const bIsNull: boolean = b === null;

  if (aIsNull && bIsNull) {
    return 0;
  }

  if (bIsNull) {
    // a is undefined > b is null
    return ascending ? 1 : -1;
  }

  if (aIsNull) {
    // a is null < b is undefined
    return ascending ? -1 : 1;
  }

  return 0; // both are undefined
}

/**
 * Generic comparison function type
 */
export type ObjectComparisonFunction = (A: any, B: any) => number;

/**
 * Generates a comparator function   cmp(A,B): number   based on its parameters. eg: for array sort.
 * The generated function returns -1, 0 or 1 depending on the comparison of A and B.
 * It uses lodash _.get to extract the compared values using path syntax from A and B.
 * Examples of valid paths are:  'name', 'projectList.[3].name', 'project.name'.
 * It accepts multiple parameter types for 'path':
 *  - with a single string it extracts that path for comparison. eg: 'project.name'
 *       eg: 'project.status'
 *  - with a string array it extracts and compares each of the path in the array
 *       eg: [ 'projectList.[3].name', 'project.status']
 *  - with an array of arrays of 2 elements, it uses the first element as the path and the second as the sort direction.
 *       eg: [ ['projectList.[3].name', true], [ 'project.status', true] ]
 *  - with an array of arrays of 3 elements, it uses the first element as the path and the second as the sort direction, the third sets ignoreCase.
 *       eg: [ ['projectList.[3].name', true, false], [ 'project.status', true, false] ]
 *  Mixing the parameter types is not allowed.
 *  Both ascending and descending sorting is supported.
 *  The generated function uses compareBasicTypes for the comparison
 *
 * @param paths       - defines which properties in each element to compare
 * @param ascending  - (true)  if true compare for ascending sort (A <= B), else for descending.
 * @param ignoreCase - (false) if true compare all in lower case
 */
const rxArrayPath = new RegExp("\\[(\\d+)]");
export const splitPath = (P: string): (string | number)[] => {
  return P.split(".").map((s) => {
    const m = s.match(rxArrayPath);
    if (m != null) {
      return parseInt(m[1]);
    }
    return s;
  });
};

export function generateAttributeStringCompare(
  paths: string | string[] | [string, boolean][] | [string, boolean, boolean][],
  ascending = true,
  ignoreCase = false,
): ObjectComparisonFunction {
  if (!paths) {
    return (_A: any, _B: any): number => {
      return 0;
    };
  } else if (typeof paths === "string") {
    // Comparing by exactly one attribute.
    //   eg:  let compFn =  generateAttributeStringCompare(  '')
    return (A: any, B: any): number => {
      return compareBasicTypes(path(splitPath(paths), A), path(splitPath(paths), B), ascending, ignoreCase);
    };
  } else if (paths.length > 0 && typeof paths[0] === "string") {
    // Comparing by multiple attributes.
    //   eg:  let compFn =  generateAttributeStringCompare( [ 'projects.[1].name','owner', 'group.name' ])
    return (A: any, B: any): number => {
      let cmp = 0; // value if the path array is empty.
      let pos = 0;

      // loop as long through the fields until a difference is detected (0 indicates equal)
      while (
        pos < paths.length &&
        0 ===
          (cmp = compareBasicTypes(
            path(splitPath(paths[pos] as string), A),
            path(splitPath(paths[pos] as string), B),
            ascending,
            ignoreCase,
          ))
      ) {
        ++pos;
      }

      return cmp;
    };
  } else if (
    paths.length > 0 &&
    paths[0].length === 2 &&
    typeof paths[0][0] === "string" &&
    typeof paths[0][1] === "boolean" // ascending order
  ) {
    // Comparing by multiple attributes with separate sort directions for each,
    //   eg:  let compFn =  generateAttributeStringCompare( [ ['projects.[1].name', true], ['owner', false], ['group.name', false] ]);
    return (A: any, B: any): number => {
      let cmp = 0; // value if the path array is empty.
      let pos = 0;

      // loop as long through the fields until a difference is detected (0 indicates equal)
      while (
        pos < paths.length &&
        0 ===
          (cmp = compareBasicTypes(
            path(splitPath(paths[pos][0]), A),
            path(splitPath(paths[pos][0]), B),
            paths[pos][1] as boolean,
            ignoreCase,
          ))
      ) {
        ++pos;
      }

      return cmp;
    };
  } else if (
    paths.length > 0 &&
    paths[0].length === 3 &&
    typeof paths[0][0] === "string" &&
    typeof paths[0][1] === "boolean" && // ascending order
    typeof paths[0][2] === "boolean" // case insensitive
  ) {
    // Comparing by multiple attributes with separate sort directions for each, and 'projects.[1].name' compares case-insensitive
    //   eg:  let compFn =  generateAttributeStringCompare( [ ['projects.[1].name', true, true], ['owner', false], ['group.name', false] ], true, false);
    return (A: any, B: any): number => {
      let cmp = 0; // value if the path array is empty.
      let pos = 0;

      // loop as long through the fields until a difference is detected (0 indicates equal)
      while (
        pos < paths.length &&
        0 ===
          (cmp = compareBasicTypes(
            path(splitPath(paths[pos][0]), A),
            path(splitPath(paths[pos][0]), B),
            paths[pos][1] as boolean, //
            paths[pos][2] as boolean, // case insensitive
          ))
      ) {
        ++pos;
      }

      return cmp;
    };
  } else {
    // Cannot generate comparison function for path
    return (_A, _B): number => {
      return 0;
    };
  }
}

/**
 * Compare two arrays of objects and return -1,0,1 depending on the elementwise comparison and length difference.
 * Returns 0 if the arrays have the same size and all elements are identical and in the same order.
 * Iterates synchronously over the elements in both array and returns the value of the comparison with cmpFn if they are not identical.
 * When ignoreLength is false it compares only the part of array that is present in both arrays.
 * Otherwise a length difference always returns the difference of b.length-a.length.
 * Immediately returns if the arrays have different length unless ignoreLength is true.
 *
 * @param a     Array of T objects
 * @param b     Array of T objects
 * @param cmpFn - the comparator function, see return values of generateAttributeStringCompare
 * @param ignoreLength  - (false), if true compare only the part of the array that is present in both.
 */
export function compareArrays<T>(a: T[], b: T[], cmpFn: ObjectComparisonFunction, ignoreLength = false): number {
  let limit = a.length;
  if (a.length !== b.length) {
    if (!ignoreLength) {
      return b.length - a.length;
    }
    limit = Math.min(a.length, b.length);
  }
  // TBD: add position independent compare using lodash  xorWith() + length of returned array
  for (let i = 0; i < limit; ++i) {
    const rc = cmpFn(a[i], b[i]);
    if (rc !== 0) {
      return rc;
    }
  }
  return 0;
}

export function pluralizeWord(name: string, count?: number) {
  return pluralize(name, count);
}

// Returns message "N characters left" or an empty string based on the max length and a display limit.
//   If displayLimit is null then message is returned once the currLength > 0.
//   If displayLimit is a positive number the message is returned if the remaining characters go below the limit.
export function charsLeftMessage(maxLength: number, currLength: number, displayLimit: number) {
  if (!currLength) {
    return;
  }
  const charsLeft = Math.max(0, maxLength - currLength);

  return (!displayLimit && currLength > 0) || displayLimit >= charsLeft
    ? charsLeft + " " + pluralizeWord("character", charsLeft) + " left"
    : "";
}

/**
 * Test if the lower case version of text contains term (expected to be lower case).
 *   Returns true if text is not null or undefined and the lower case text contains the term.
 *   Term is not converted to lower case and is not tested for null or undefined.
 *   Returns false if text is null or undefined (is falsy).
 * @param text           string, will be searched in its lower case form
 * @param lowerCaseTerm  string, the term searched in text
 * @returns {boolean},   true if term is contained in text
 */
export function containsInLowerCase(text: string | undefined | null, lowerCaseTerm: string): boolean {
  return !!text && text.toLowerCase().includes(lowerCaseTerm);
}

/**
 * Return a string based on a boolean value
 *   Used in the UI for display of booleans.
 * @param value    a boolean value
 * @returns {string}   the string representing the value, either 'yes' or 'no'
 */
export function booleanToString(value: boolean): string {
  return value ? "yes" : "no";
}

/**
 * Breakup a line into a string array. Each piece is shorter than hardLimit chars.
 *   Avoids breaking up words if the start in the range [hardLimit-graceRange..hardLimit]
 *   Lines are can have hardLimit-graceRange .. hardLimit characters.
 * @param line   the text to split
 * @param hardLimit  maximum length of each line
 * @param graceRange length of the flexible break.
 * @returns {string[]}  the list of lines created from line
 */
export function smartSplitToLines(line: string, hardLimit: number, graceRange = 10): string[] {
  if (!line) {
    return [];
  }
  const limit = Math.max(hardLimit, 0);
  const grace = Math.max(graceRange, 0);
  const firstBreak = grace > limit ? 0 : limit - grace;
  const list: string[] = [];
  let buffer = line;

  while (buffer.length > 0) {
    const index = buffer.indexOf(" ", firstBreak);
    const pos = Math.min(index === -1 ? limit : index, limit);
    const part = buffer.substr(0, pos);

    list.push(part);
    buffer = buffer.substr(part.length + (buffer.charAt(pos) === " " ? +1 : 0));
  }

  return list;
}

export function getSourceString(genome: Organism, database: Database): string {
  // returns either the genome name or, if not assigned to a genome, the database name
  if (genome && genome.name !== UNKNOWN_GENOME_NAME) {
    return genome.name;
  } else if (database) {
    return database.name;
  } else {
    return null;
  }
}

export const EMAIL_REGEX = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;

export function matchesEmail(email: string) {
  return EMAIL_REGEX.test(email);
}

export function getSavedItemTypeLabel(savedItemsListing: Pick<SavedItemsListing, "type" | "name">): string {
  return savedItemsListing?.type === ENTITY_TYPE.ANALYST_SESSION ||
    savedItemsListing?.type === ENTITY_TYPE.VIEW ||
    savedItemsListing?.type === ENTITY_TYPE.REGION_FLAG ||
    savedItemsListing?.type === ENTITY_TYPE.SAMPLE_PARENT ||
    savedItemsListing?.type === ENTITY_TYPE.COORD_LIST
    ? savedItemsListing.name
    : ITEMS_TYPE.GROUP;
}

export function getFullTypeLabel(savedItemsListing: SavedItemsListing, count?: number): string {
  const category: string = getSavedItemTypeLabel(savedItemsListing);
  const itemCount: number = count | savedItemsListing.count;
  return (category === ITEMS_TYPE.GROUP ? savedItemsListing.name + " " : "") + pluralizeWord(category, itemCount);
}

// TODO: move to viewer utils?
export function conditionallyPluralizeTrackLabel(track: ViewerTrack): string {
  // Pluralize only the entity-related tracks.
  // All other labels are taken as they are (change in gen_nu_feature_type/gen_aa_feature_type if required)
  switch (track.type) {
    case TRACK_TYPE.GENE:
    case TRACK_TYPE.PROTEIN_LOCATION:
    case TRACK_TYPE.TRANSCRIPT_LOCATION:
    case TRACK_TYPE.VARIANT:
      return pluralize.plural(track.name ?? "Unknown");
    default:
      return track.name ?? "Unknown";
  }
}

export function rightPad(text: string | null, minLen: number, filler: string | null = " ") {
  let f = filler;

  if (!filler || filler.length === 0) {
    // Cannot use a zero width filler, using SPACE instead
    f = " ";
  }

  let t = text;

  if (!text && (text === null || text === undefined)) {
    // Cannot pad a text of null or undefined, using empty string instead.
    t = "";
  }

  if (minLen <= 0) {
    // if (minLen < 0) rightPad: Cannot fill to a negative length, returning original text
    return t;
  }

  const missing = minLen - t.length;
  const fullBlocks = Math.max(0, Math.floor(missing / f.length));
  const remainder = missing - fullBlocks * f.length;
  const buffer = repeat(f, fullBlocks).join("") + (remainder > 0 ? f.substr(0, remainder) : "");

  return t + buffer;
}

export function leftPad(text: string | null, minLen: number, filler: string | null = " ") {
  if (minLen <= 0) {
    // if (minLen < 0) Cannot fill to a negative length, returning original text
    return text;
  }

  let f = filler;

  if (!filler || filler.length === 0) {
    //Cannot use a zero width filler, using SPACE instead
    f = " ";
  }

  let t = text;

  if (!text && (text === null || text === undefined)) {
    // Cannot pad a text of null or undefined, using empty string instead.
    t = "";
  }

  const missing = minLen - t.length;
  const fullBlocks = Math.max(0, Math.floor(missing / f.length));
  const remainder = missing - fullBlocks * f.length;
  const buffer = repeat(f, fullBlocks).join("") + (remainder > 0 ? f.substr(0, remainder) : "");

  return buffer + t;
}

export function escapeRegexCharacters(str: string): string {
  // http://kevin.vanzonneveld.net
  // +   original by: booeyOH
  // +   improved by: Ates Goral (http://magnetiq.com)
  // +   improved by: Kevin van Zonneveld (http://kevin.vanzonneveld.net)
  // +   bugfixed by: Onno Marsman
  // *     example 1: preg_quote("$40");
  // *     returns 1: '\$40'
  // *     example 2: preg_quote("*RRRING* Hello?");
  // *     returns 2: '\*RRRING\* Hello\?'
  // *     example 3: preg_quote("\\.+*?[^]$(){}=!<>|:");
  // *     returns 3: '\\\.\+\*\?\[\^\]\$\(\)\{\}\=\!\<\>\|\:'

  return (str + "").replace(/([\\.+*?[^\]$(){}=!<>|:])/g, "\\$1");
}

export function highlightMatchingSubstring(data: string, search: string): string {
  if (!data || !search) {
    return data;
  }

  return data.replace(new RegExp("(" + escapeRegexCharacters(search) + ")", "gi"), "<span class='highlight'>$1</span>");
}

export function highlightStartsWith(data: string, search: string): string {
  if (!data || !search) {
    return data;
  }

  return data.replace(new RegExp("^(" + escapeRegexCharacters(search) + ")", "i"), "<span class='highlight'>$1</span>");
}

export function capitalizeFirstLetter(label: string): string {
  return label && label.charAt(0).toUpperCase() + label.slice(1);
}

// allow to upper case all words e.g. in case of BLAST hit => BLAST Hit
export function capitalizeFirstLetterOfAllWords(label: string): string {
  return label.replace(/\b\w/g, (match) => match.toUpperCase());
}

/**
 * Capitalize every word in str unless it is found in exceptions.
 * @returns {string}    the capitalized text
 * @params  str         text to capitalize
 * @params  exeptions   list of verbatim words
 * @deprecated Use titleCase(...) from title-case module
 * */
export function toTitleCase(str: string, exceptions: string[] = []): string {
  return str.replace(/\w\S*/g, (txt) =>
    exceptions.includes(txt) ? txt : txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase(),
  );
}

export function trimOrThrow(value?: string | null, error?: Error): string | null | undefined {
  // null or undefined is ok
  if (value == null) {
    return value;
  }

  const r = value.trim();

  // a trimmed string that results in zero length is not ok if error is defined
  // for values like description, it can be ok to silently convert this to undefined
  if (r.length === 0) {
    if (error) {
      throw error;
    }

    return null;
  }

  // otherwise returned the trimmed value
  return r;
}

export function getItemCountString(content: ContentBrowser | AdminBrowser, eventKey: string) {
  return content?.[eventKey]?.count ? content[eventKey].count : "";
}

export function checkSearchValueTermsLength(value: string, minNumberOfChars: number): boolean {
  const tooShortTerm = splitIntoMultipleSearchTerms(value).find((term: string) => term.length < minNumberOfChars);
  return !tooShortTerm;
}

export function splitIntoMultipleSearchTerms(term: string): string[] {
  return term.trim().split(/\s+/);
}

export function extractNumberFromString(value: string): number {
  if (!value) {
    return null;
  }
  return Number(value.replace(/\D/g, ""));
}

export function flattenObj(obj, parent?, res = {}) {
  for (const key of Object.keys(obj)) {
    const propName = parent ? parent + "." + key : key;
    if (typeof obj[key] === "object") {
      flattenObj(obj[key], propName, res);
    } else {
      res[propName] = obj[key];
    }
  }
  return res;
}

export function createNuSequenceLengthString(length): string {
  return typeof length === "number" && length > 0 ? `${formatNumberByThousands(length)} bp` : "No sequence available";
}

/**
 * Check if string contains non-ascii chars or unprintable chars from ascii
 * It accepts SPACE (0x20) .. TILDE (0x7E) and NL (0x0A) and CR (0xOD) and TAB (0x09)
 * @param value
 * @return  true for UTF-8 chars
 */
export function isNotASCII(value: string): boolean {
  // eslint-disable-next-line no-control-regex
  return /[^\x0A\x0D\x09\x20-\x7e]/.test(value);
}

/**
 *  Convert text to lower case, but replace the acronyms using the mapping in the vocabulary.
 *  The default vocabulary knows EC, NCBI, GeneData, GeneBank ....
 * @returns {string},   the converted text
 * @param label         the text to convert to lower case
 * @param voc           mapping from uppercase version of acronyms to the ususal form
 */
export function lowerCaseIfNotAcronym(label: string, voc: object = defaultMapOfAcronyms): string {
  if (!label) {
    return;
  }

  return label.replace(/\w\S*/g, (txt) => {
    if (voc[txt.toUpperCase()]) {
      return voc[txt.toUpperCase()];
    } else {
      return txt.toLowerCase();
    }
  });
}

export { escapeString, unescapeString };
